import React from "react";
import styled from "styled-components";
import { Dispute, EyeIcon, Triangle, UrgentIcon } from "../../assets";
import TableSkeleton from "./TableSkeleton";

const TableWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  box-sizing: border-box;
  width: 100%;
  overflow-x: auto;
`;

const TableHead = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  border-bottom: solid 1px rgba(160, 155, 155, 0.1);
  align-self: stretch;
  box-sizing: border-box;
  padding: 8px 0px;
  flex-shrink: 0;
  min-width: fit-content;
  width: 100%;
`;

const TableRow = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
  min-width: fit-content;
  width: 100%;
`;
const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.align || "flex-start"};
  flex: ${(props) => (props.flex ? "1" : "none")};
  gap: 8px;
  width: ${(props) => props.width || "auto"};
  box-sizing: border-box;

  /* Allow overflow for tooltips but clip text */
  overflow: visible;
  position: relative;

  min-width: 0;
  max-width: ${(props) => props.width || "100%"};

  @media (max-width: 768px) {
    min-width: 100px;
    flex: 1;
  }
`;
const Text = styled.span`
  color: ${(props) => props.color || "rgb(33, 37, 67)"};
  font-size: ${(props) => props.fontSize || "16px"};
  font-family: Montserrat, sans-serif;
  font-weight: ${(props) => props.fontWeight || "500"};
  line-height: 160%;
  text-align: left;

  /* Prevent text overflow */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 100%;
  display: block;
`;

// Wrapper for text cells with hover tooltip
const TextCellWrapper = styled.div`
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  min-width: 0;
  flex: 1;
  display: block;

  &:hover .cell-tooltip {
    visibility: visible;
    opacity: 1;
  }
`;

// Wrapper for referral_id cells with icons - moved here for proper ordering
const ReferralIdWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
`;

// Text for referral ID - truncates but doesn't push icons away
const RefIdText = styled.span`
  color: ${(props) => props.color || "rgb(33, 37, 67)"};
  font-size: ${(props) => props.fontSize || "16px"};
  font-family: Montserrat, sans-serif;
  font-weight: ${(props) => props.fontWeight || "500"};
  line-height: 160%;
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
`;

const CellTooltip = styled.div`
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s ease;
  position: absolute;
  z-index: 1000;
  bottom: calc(100% + 8px);
  left: 0;
  background: rgb(33, 37, 67);
  color: rgb(255, 255, 255);
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  white-space: normal;
  word-break: break-word;
  max-width: 280px;
  min-width: 120px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  pointer-events: none;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 16px;
    border-width: 6px;
    border-style: solid;
    border-color: rgb(33, 37, 67) transparent transparent transparent;
  }
`;

const TextHead = styled.span`
  color: ${(props) => props.color || "rgb(33, 37, 67)"};
  font-size: ${(props) => props.fontSize || "16px"};
  font-family: Montserrat, sans-serif;
  font-weight: ${(props) => props.fontWeight || "500"};
  line-height: 160%;
  text-align: left;

  /* Enable wrapping */
  word-break: break-word;
  white-space: normal;
`;
const Viewreason = styled.span`
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: 0%;
  color: rgba(167, 4, 4, 1);
`;

const StatusTag = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  align-self: stretch;
  background: ${(props) => {
    switch (props.status) {
      case "successful":
        return "linear-gradient(-90deg, rgba(18, 124, 78, 0.1), rgba(18, 124, 78, 0.1))";
      case "rewarded":
        return "linear-gradient(-90deg, rgba(18, 124, 78, 0.1), rgba(18, 124, 78, 0.1))";
      case "tracking":
        return "linear-gradient(-90deg, rgba(244, 186, 52, 0.1), rgba(244, 186, 52, 0.1))";
      case "pending":
        return "linear-gradient(-90deg, rgba(244, 186, 52, 0.1), rgba(244, 186, 52, 0.1))";
      case "unsuccessful":
        return "linear-gradient(-90deg, rgba(240, 70, 63, 0.1), rgba(240, 70, 63, 0.1))";
      case "admin rejected":
        return "linear-gradient(-90deg, rgba(240, 70, 63, 0.1), rgba(240, 70, 63, 0.1))";
      case "paid":
        return "rgba(89, 84, 222, 0.1)";
      default:
        return "transparent";
    }
  }};
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 4px 8px;
  flex-shrink: 0;
`;

const StatusText = styled.span`
  color: ${(props) => {
    switch (props.status) {
      case "successful":
        return "rgb(18, 124, 78)";
      case "rewarded":
        return "rgb(18, 124, 78)";
      case "tracking":
        return "rgb(244, 186, 52)";
      case "pending":
        return "rgb(244, 186, 52)";
      case "unsuccessful":
        return "rgb(240, 70, 63)";
      case "admin rejected":
        return "rgb(240, 70, 63)";
      case "paid":
        return "rgba(89, 84, 222, 1)";
      default:
        return "inherit";
    }
  }};
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const Table = ({ headers, data, columns, onViewReason, loading }) => {
  const renderCell = (column, item) => {
    const status = item.status?.toLowerCase(); // assuming status is under 'status' key
    const reward_status = item.reward_status?.toLowerCase(); // assuming status is under 'status' key

    if (column.type === "status") {
      const statusValue = item[column.key] || "";
      return (
        <StatusTag status={status} title={statusValue}>
          <StatusText status={status}>{statusValue}</StatusText>
        </StatusTag>
      );
    }
    if (column.key === "reward_status") {
      const rewardValue = item[column.key] || "";
      const displayReward = rewardValue.charAt(0).toUpperCase() + rewardValue.slice(1);
      return (
        <StatusTag status={reward_status} title={displayReward}>
          <StatusText status={reward_status}>
            {displayReward}
          </StatusText>
        </StatusTag>
      );
    }

    // // Handle reward display logic based on status
    // if (column.key === "vendor_reward") {
    //   if (status === "unsuccessful") {
    //     return (
    //       <Viewreason as="span" onClick={() => onViewReason(item)}>
    //         View Reason
    //       </Viewreason>
    //     );
    //   } else if (status === "successful") {
    //     return <Text>{item.vendor_reward}</Text>;
    //   } else if (status === "paid") {
    //     return <Text>{item.vendor_reward}</Text>;
    //   } else if (status === "tracking") {
    //     return <Text>-</Text>;
    //   }
    // }

    if (column.key === "action") {
      return (
        <EyeIconImage
          alt="Eye"
          src={EyeIcon}
          onClick={(e) => {
            e.stopPropagation(); // prevent triggering row click
            onViewReason(item);
          }}
        />
      );
    }

    if (column.key === "referral_id") {
      const refId = item[column.key] || "";
      return (
        <ReferralIdWrapper>
          <RefIdText
            color={column.color}
            fontSize={column.fontSize}
            fontWeight={column.fontWeight}
            title={refId}
          >
            {refId}
          </RefIdText>
          {item.dispute && item.dispute.dispute_status === "Open" && (
            <TooltipWrapper>
              <GFeildsInfo src={Dispute} alt="icon" />
              <TooltipText className="tooltip">
                This referral has a dispute.
              </TooltipText>
            </TooltipWrapper>
          )}
          {item.is_urgent && (
            <TooltipWrapper>
              <GFeildsInfo src={UrgentIcon} alt="icon" />
              <TooltipText className="tooltip">Urgent: time-sensitive referral. Quick response requested.</TooltipText>
            </TooltipWrapper>
          )}
        </ReferralIdWrapper>
      );
    }

    // Default text render with hover tooltip for truncated text
    const cellValue = item[column.key];
    const displayValue = cellValue != null ? String(cellValue) : "";

    return (
      <Text
        color={column.color}
        fontSize={column.fontSize}
        fontWeight={column.fontWeight}
        title={displayValue}
      >
        {displayValue}
      </Text>
    );
  };

  return (
    <TableWrapper>
      <TableHead>
        {headers.map((header, index) => (
          <Cell
            key={index}
            width={header.width}
            flex={header.flex}
            align={header.align}
          >
            <TextHead color="rgb(100, 116, 139)">{header.label}</TextHead>
          </Cell>
        ))}
      </TableHead>
      {loading ? (
        <TableSkeleton columns={columns} rows={5} />
      ) : data.length > 0 ? (
        data.map((item, rowIndex) => (
          <TableRow key={rowIndex} style={{ cursor: "pointer" }}>
            {columns.map((column, colIndex) => (
              <Cell
                key={colIndex}
                width={column.width}
                flex={column.flex}
                align={column.align}
              >
                {renderCell(column, item)}
              </Cell>
            ))}
          </TableRow>
        ))
      ) : (
        <TableRow>
          <Cell
            style={{
              textAlign: "center",
              padding: "16px",
              width: "100%",
              justifyContent: "center",
            }}
            flex
          >
            <Text>No data found</Text>
          </Cell>
        </TableRow>
      )}
    </TableWrapper>
  );
};

export default Table;

const TooltipText = styled.div`
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.3s;
  color: #000;
  font-weight: 500;
  font-size: 12px;
  text-align: start;
  padding: 8px 16px;
  border-radius: 4px;
  position: absolute;
  z-index: 9999;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  max-width: 280px;
  width: 200px;
  background: #ffffff;
  backdrop-filter: blur(8px);
  font-family: "Montserrat", sans-serif;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  white-space: normal;

  &::after {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent #ffffff transparent;
  }
`;

const GFeildsInfo = styled.img`
  width: 18px;
  height: 18px;
  object-fit: cover;
  cursor: pointer;
`;
const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 1px;
  flex-shrink: 0;

  &:hover ${TooltipText} {
    visibility: visible;
    opacity: 1;
  }
`;
const EyeIconImage = styled.img`
  transition: transform 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.4);
  }
`;
